import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useEffect } from "react";
import { Icon, Modal, Switch } from "../";
import { useToast } from "@finitless/shared/hooks";
import {
  handleApiError,
  handleApiSuccess,
  stringToSlug,
  convertFileToBase64,
} from "@finitless/shared/lib";
import { DeleteConfirmationModal } from "../Modals";
import {
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from "../../../hooks/useProducts";

const CreateCategoryModal = ({
  isOpen,
  onClose,
  businessId,
  selectedBusinessSlug,
  categoryToEdit = null,
  onToggleCategory,
  onCategoryDeleted,
  isLoadingToggle,
  hasPosIntegration,
}) => {
  const toast = useToast();
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();
  const deleteMutation = useDeleteCategory();
  const isEditMode = !!categoryToEdit;

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(categoryToEdit);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    image: null,
  });
  const [errors, setErrors] = useState({});

  // Initialize form data when categoryToEdit changes
  useEffect(() => {
    if (categoryToEdit) {
      setCurrentCategory(categoryToEdit);
      setFormData({
        name: categoryToEdit.name || "",
        description: categoryToEdit.description || "",
        image: categoryToEdit.image || null,
      });
    } else {
      setCurrentCategory(null);
      setFormData({
        name: "",
        description: "",
        image: null,
      });
    }
    setErrors({});
  }, [categoryToEdit, isOpen]);

  const handleImageChange = async (file) => {
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      if (file.size > 512 * 1024) {
        toast.error("Image size must be less than 512KB");
        return;
      }

      try {
        const base64String = await convertFileToBase64(file);
        setFormData((prev) => ({ ...prev, image: base64String }));
      } catch (error) {
        console.error("Error converting logo to base64:", error);
        toast.error("Error processing image");
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validateForm()) {
      toast.error("Please fix the errors");
      return;
    }

    try {
      const payload = {
        name: formData.name?.trim(),
        description: formData.description?.trim() || "",
        slug: stringToSlug(formData.name || ""),
      };
      // Only include image if a new image was uploaded (base64)
      if (
        formData.image &&
        typeof formData.image === "string" &&
        formData.image.startsWith("data:image/")
      ) {
        payload.image = formData.image;
      }

      if (isEditMode) {
        await updateMutation.mutateAsync({
          businessId,
          categoryId: categoryToEdit.id,
          payload,
          slug: selectedBusinessSlug,
        });
        handleApiSuccess("Category updated successfully", toast);
      } else {
        await createMutation.mutateAsync({
          businessId,
          payload,
          slug: selectedBusinessSlug,
        });
        handleApiSuccess("Category created successfully", toast);
      }
      handleClose();
    } catch (error) {
      handleApiError(error, toast, {
        title: isEditMode
          ? "Failed to Update Category"
          : "Failed to Create Category",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleDelete = () => {
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!currentCategory) return;
    try {
      const deletedCategoryId = currentCategory.id;
      await deleteMutation.mutateAsync({
        businessId,
        categoryId: deletedCategoryId,
        slug: selectedBusinessSlug,
      });
      handleApiSuccess("Category deleted successfully", toast);
      setIsDeleteOpen(false);
      if (onCategoryDeleted) {
        onCategoryDeleted(deletedCategoryId);
      }
      handleClose();
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to Delete Category",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleToggleEnabled = async (value) => {
    if (!currentCategory || !onToggleCategory) return;
    try {
      await onToggleCategory(currentCategory.id, value);
      // Update local state after successful toggle
      setCurrentCategory({ ...currentCategory, enabled: value });
    } catch {
      // Error handling is done in the parent component
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      image: null,
    });
    setErrors({});
    setIsDeleteOpen(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? "Edit Category" : "Create Category"}
      subtitle={
        isEditMode
          ? "Update category details"
          : "Add a new category to this business"
      }
      maxWidth="max-w-md"
      className="ordering-catalog-surface"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-foreground mb-2 block text-sm font-medium">
            Image
          </label>
          <p className="text-muted-foreground mb-3 text-xs">
            Accepted formats: PNG, JPG, JPEG, WEBP (Max 512KB)
          </p>
          <div className="flex items-center justify-center gap-3">
            <div className="relative inline-block">
              {formData.image ? (
                <>
                  <img
                    src={formData.image}
                    alt="Preview"
                    className="border-border h-24 w-24 rounded-lg border-2 object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40">
                    <div className="flex items-center space-x-1">
                      <Icon name="Image" className="h-5 w-5 text-white" />
                      <Icon
                        name="MoreVertical"
                        className="h-5 w-5 text-white"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="border-border bg-card flex h-24 w-24 items-center justify-center rounded-lg border">
                  <Icon
                    name="Image"
                    className="text-muted-foreground h-6 w-6"
                  />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e.target.files?.[0])}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                disabled={createMutation.isPending || updateMutation.isPending}
                aria-label="Upload image"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="text-foreground mb-1 block text-sm font-medium">
            Name <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
            placeholder="Category name"
            required
          />
          {errors.name && (
            <div className="text-destructive mt-1 text-xs">{errors.name}</div>
          )}
        </div>

        <div>
          <label className="text-foreground mb-1 block text-sm font-medium">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="border-border bg-card focus:ring-ring text-foreground w-full resize-none rounded-lg border px-3 py-2 placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
            placeholder="Short description"
            rows={3}
          />
        </div>

        {/* Actions only shown in edit mode */}
        {isEditMode && !hasPosIntegration && (
          <>
            <div className="border-border border-t pt-4">
              <div className="space-y-4">
                <div className="border-border bg-card flex w-full items-center justify-between rounded-lg border px-4 py-2">
                  <span className="text-foreground text-sm font-medium">
                    {currentCategory?.enabled ? "Enabled" : "Disabled"} Category
                  </span>
                  <Switch
                    id="category-enabled"
                    checked={!!currentCategory?.enabled}
                    onCheckedChange={handleToggleEnabled}
                    disabled={
                      isLoadingToggle ||
                      createMutation.isPending ||
                      updateMutation.isPending
                    }
                  />
                </div>
                <Button
                  emphasis="solid"
                  intent="danger"
                  type="button"
                  onClick={handleDelete}
                  disabled={
                    deleteMutation.isPending ||
                    createMutation.isPending ||
                    updateMutation.isPending
                  }
                  className="flex w-full items-center justify-between gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deleteMutation.isPending ? "Deleting..." : "Delete Category"}
                  <Icon name="Trash" className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            variant="ghost"
            type="button"
            onClick={handleClose}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="text-card-foreground bg-card border-border hover:bg-accent rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            type="submit"
            disabled={
              createMutation.isPending ||
              updateMutation.isPending ||
              !formData.name.trim()
            }
            className="px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isEditMode
              ? updateMutation.isPending
                ? "Updating..."
                : "Update Category"
              : createMutation.isPending
                ? "Creating..."
                : "Create Category"}
          </Button>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={confirmDelete}
        itemName={currentCategory?.name}
        itemType="Category"
        isLoading={deleteMutation.isPending}
      />
    </Modal>
  );
};

export default CreateCategoryModal;
